'use client'

/**
 * Form Draft Hook (Issue #3227, #3781)
 *
 * Auto-saves form drafts to localStorage with debounce.
 * Restores drafts when users return to the same form.
 * Prevents data loss from accidental navigation.
 *
 * Enhanced in #3781:
 * - flushDraft(): immediately persist, bypassing debounce (for pre-submit)
 * - registerBeforeRefresh integration for safe page reloads
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { registerBeforeRefresh } from '@/lib/safe-refresh'

export const DRAFT_PREFIX = 'cadence-draft:'
export const DRAFT_DEBOUNCE_MS = 2000
/**
 * Exported since #13645 so `useFeedbackDraft` — which owns a richer draft
 * shape than this hook's single string but shares the same localStorage entry
 * format and the same `cadence-draft:` namespace — expires entries on the
 * same clock instead of restating the number.
 */
export const DRAFT_EXPIRY_MS = 24 * 60 * 60 * 1000 // 24 hours

type DraftEntry = {
  value: string
  timestamp: number
}

function loadDraft(formId: string): string | null {
  try {
    const raw = localStorage.getItem(`${DRAFT_PREFIX}${formId}`)
    if (!raw) return null

    const entry: DraftEntry = JSON.parse(raw)

    if (Date.now() - entry.timestamp > DRAFT_EXPIRY_MS) {
      localStorage.removeItem(`${DRAFT_PREFIX}${formId}`)
      return null
    }

    return entry.value
  } catch {
    return null
  }
}

function saveDraft(formId: string, value: string): void {
  try {
    const entry: DraftEntry = {
      value,
      timestamp: Date.now(),
    }
    localStorage.setItem(`${DRAFT_PREFIX}${formId}`, JSON.stringify(entry))
  } catch {
    // Silently fail
  }
}

function removeDraft(formId: string): void {
  try {
    localStorage.removeItem(`${DRAFT_PREFIX}${formId}`)
  } catch {
    // Silently fail
  }
}

/**
 * Auto-save form drafts to localStorage with debounce.
 *
 * @example
 * const { draftValue, setDraftValue, hasDraft, clearDraft } = useFormDraft('update:rock:abc')
 * <textarea value={draftValue} onChange={e => setDraftValue(e.target.value)} />
 * {hasDraft && <button onClick={clearDraft}>Discard draft</button>}
 */
export function useFormDraft(formId: string) {
  const [draftValue, setDraftValueState] = useState(
    () => loadDraft(formId) ?? '',
  )
  const [hasDraft, setHasDraft] = useState(() => loadDraft(formId) !== null)
  const debounceRef = useRef<NodeJS.Timeout | undefined>(undefined)

  const setDraftValue = useCallback(
    (value: string) => {
      setDraftValueState(value)

      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }

      if (!value.trim()) {
        setHasDraft(false)
        return
      }

      debounceRef.current = setTimeout(() => {
        saveDraft(formId, value)
        setHasDraft(true)
      }, DRAFT_DEBOUNCE_MS)
    },
    [formId],
  )

  const clearDraft = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    setDraftValueState('')
    setHasDraft(false)
    removeDraft(formId)
  }, [formId])

  // Track current value in a ref for flushDraft
  const currentValueRef = useRef(draftValue)
  currentValueRef.current = draftValue

  /**
   * Immediately persist the current draft, bypassing the debounce.
   * Call this before submitting or before a page reload.
   */
  const flushDraft = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = undefined
    }
    const value = currentValueRef.current
    if (value.trim()) {
      saveDraft(formId, value)
      setHasDraft(true)
    }
  }, [formId])

  // Cleanup timeout on unmount + register with safe-refresh
  useEffect(() => {
    const unregister = registerBeforeRefresh(() => {
      const value = currentValueRef.current
      if (value.trim()) {
        saveDraft(formId, value)
      }
    })

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      unregister()
    }
  }, [formId])

  return {
    draftValue,
    setDraftValue,
    hasDraft,
    clearDraft,
    flushDraft,
  }
}
